import { Box, Card, Grid, Title, Text, useMantineTheme, Space, Group, Stack, Tooltip, Center } from '@mantine/core';
import { BarChart, UserConfiguredBarChartSettings } from '@root/Components/Charts/BarChart';
import { PieChart } from '@root/Components/Charts/PieChart';
import { useDi } from '@root/Services/DI';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import { DiscountQueryService } from '@root/Services/Invoices/DiscountQueryService';
import { useEffect, useState } from 'react';
import { DiscountsPageContent } from './DiscountsLayoutPage';
import { InfoCircle } from 'tabler-icons-react';
import { RangeSelection } from './DiscountsPage';
import { Score, TooltipWhite } from '@root/Design/Primitives';
import { FillerSwitch } from '@root/Design/Filler';
import styled from '@emotion/styled';

type SavingsGroup = 'Commitment Savings' | 'Commitment Waste';
export function Savings(props: { platform: string; range: RangeSelection }) {
    const theme = useMantineTheme();
    const fmtSvc = useFmtSvc();
    const discountQueryService = useDi(DiscountQueryService);
    type ChartData = { label: string; value: number }[];
    const [reportData, setReportData] = useState<{
        hasLeased: boolean;
        pieData: ChartData;
        barData: ChartData;
        monthlyData: { date: string; group: SavingsGroup; value: number }[];
        kpiData: { totalSavings: number; baseLineSavings: number; mdsSavings: number };
    }>();
    const { pieData, barData, monthlyData, kpiData, hasLeased } = reportData ?? {};
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    const fetchDiscountData = async (platform: string, range: RangeSelection) => {
        try {
            setLoading(true);

            const scopedApi = discountQueryService.scopeApi(platform, range.fromMonth, range.toMonth);
            const pieDataReq = discountQueryService
                .queryBuilder()
                .where((b) => b.model.NetSavings!.gt(b.param(0)))
                .select((b) => ({ platform: b.model.Platform!, name: b.model.DisplayName, value: b.sum(b.model.NetSavings!) }))
                .execute(scopedApi)
                .then((r) => r.Results?.map((item) => ({ label: `${fmtSvc.adjustCspName(item.platform)} : ${item.name}`, value: item.value })) ?? []);
            const savingsReq = discountQueryService
                .queryBuilder()
                .select((b) => ({
                    leasedSavings: b.aggIf(b.model.IsLeased!.eq(true), b.sum(b.model.NetSavings!)),
                    countLeased: b.countIf(b.model.IsLeased!.eq(true)),
                    nonLeasedSavings: b.aggIf(b.model.IsLeased!.eq(false), b.sum(b.model.NetSavings!)),
                    coveredCost: b.sum(b.model.CoveredOnDemandCost!),
                }))
                .execute(scopedApi);
            const monthlyDataReq = discountQueryService
                .queryBuilder()
                .select((b) => ({ date: b.truncDate('month', b.model.Date!, 0, range.inclFrom, range.inclTo), value: b.sum(b.model.NetSavings!) }))
                .execute(scopedApi)
                .then(
                    (r) =>
                        r.Results?.map(({ date: rawDate, value }) => {
                            const group: SavingsGroup = value > 0 ? 'Commitment Savings' : 'Commitment Waste';
                            const date = fmtSvc.formatYearMonth(fmtSvc.parseDateNoTime(rawDate));
                            return { date, group, value };
                        }) ?? []
                );

            const [pieData, savingsData, monthlyData] = await Promise.all([pieDataReq, savingsReq, monthlyDataReq]);

            const savings = savingsData.Results?.[0];
            const totalSavings = (savings?.leasedSavings ?? 0) + (savings?.nonLeasedSavings ?? 0);
            const baseLineSavings = savings?.nonLeasedSavings ?? 0;
            const coveredCost = savings?.coveredCost ?? 0;
            const mdsSavings = savings?.leasedSavings ?? 0;
            const hasLeased = !!(savings?.countLeased ?? 0);

            const kpiData = {
                totalSavings,
                baseLineSavings,
                mdsSavings,
            };
            const barData = [
                { label: 'Baseline', value: (baseLineSavings / coveredCost) * 100 },
                { label: 'Current', value: (totalSavings / coveredCost) * 100 },
            ];

            setReportData({ pieData, barData, monthlyData, kpiData, hasLeased });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (props.platform || props.range.months.length) {
            fetchDiscountData(props.platform, props.range);
        }
    }, [props.platform, props.range]);

    useEffect(() => {
        setNoData(pieData?.length === 0 && barData?.length === 0 && monthlyData?.length === 0);
    }, [pieData, barData, monthlyData]);

    return (
        <DiscountsPageContent>
            <FillerSwitch loading={loading} noData={noData} noDataMessage="Data is not yet available. Please check back later.">
                {() => (
                    <Stack sx={{ height: '100%' }}>
                        <Grid align="stretch">
                            <Grid.Col span={4}>
                                <Card radius="lg" sx={{ background: theme.colors.success[5], color: '#fff', height: '100%' }} withBorder>
                                    <Title order={4}>Total Savings</Title>
                                    <SavingsKpiChart
                                        totalSavings={kpiData?.totalSavings ?? 0}
                                        baseLineSavings={kpiData?.baseLineSavings ?? 0}
                                        mdsSavings={hasLeased ? kpiData?.mdsSavings : undefined}
                                    />
                                </Card>
                            </Grid.Col>
                            <Grid.Col span={4}>
                                <Card radius="lg" withBorder pb={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Group sx={{ flex: 0 }}>
                                        <Title order={4}>Effective Savings Rate</Title>
                                        <TooltipWhite
                                            label={
                                                <>
                                                    The Effective Savings Rate is a KPI to track the efficiency of using commitment discounts.
                                                    <Space h="xs" />
                                                    The KPI calculation is: <br />
                                                    (List Cost – Discounted Cost) / List Cost
                                                </>
                                            }
                                            withinPortal
                                        >
                                            <span>
                                                <InfoCircle size={18} color={theme.colors.gray[5]} />
                                            </span>
                                        </TooltipWhite>
                                    </Group>

                                    <Box px="sm" sx={{ flex: 1, minHeight: 0, maxHeight: 220 }}>
                                        <SavingsBarChart data={barData ?? []} />
                                    </Box>
                                </Card>
                            </Grid.Col>
                            <Grid.Col span={4}>
                                <Card radius="lg" withBorder>
                                    <Title order={4}>Savings by Category</Title>
                                    <Center>
                                        <SavingsPieChart data={pieData ?? []} />
                                    </Center>
                                </Card>
                            </Grid.Col>
                        </Grid>
                        <Card radius="lg" sx={{ flex: 1 }} withBorder p={0}>
                            <Stack sx={{ height: '100%' }} spacing={0}>
                                <Title px="md" py="xs" m={0} order={4} sx={{ borderBottom: `solid 1px ${theme.colors.gray[3]}` }}>
                                    Monthly Savings
                                </Title>
                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <MonthlySavingsChart data={monthlyData ?? []} />
                                </Box>
                            </Stack>
                        </Card>
                    </Stack>
                )}
            </FillerSwitch>
        </DiscountsPageContent>
    );
}
interface SavingsKpiProps {
    totalSavings: number;
    baseLineSavings: number;
    mdsSavings?: number;
}
export function SavingsKpiChart(props: SavingsKpiProps) {
    const { totalSavings, baseLineSavings, mdsSavings } = props;
    const fmtSvc = useDi(FormatService);
    return (
        <Stack spacing={8}>
            <SavingsKpiItem>
                <Score maxSize="3rem" value={fmtSvc.formatMoneyNoDecimals(totalSavings)} />
                <Text size="sm" weight={400}>
                    Total Savings
                </Text>
            </SavingsKpiItem>
            <SavingsKpiItem>
                <Score value={fmtSvc.formatMoneyNoDecimals(baseLineSavings)} />
                <Text size="sm" weight={400}>
                    Baseline Savings
                </Text>
            </SavingsKpiItem>
            {mdsSavings === undefined ? null : (
                <SavingsKpiItem>
                    <Score value={fmtSvc.formatMoneyNoDecimals(mdsSavings)} />
                    <Text size="sm" weight={400}>
                        MDS Savings
                    </Text>
                </SavingsKpiItem>
            )}
        </Stack>
    );
}
const SavingsKpiItem = styled.div`
    display: grid;
    grid-template-columns: 1fr 120px;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    max-width: 100%;

    & > :first-child {
        justify-self: end;
        overflow: hidden;
        max-width: 100%;
        text-align: right;
        > div {
            transform-origin: left center !important;
        }
    }
    & > :last-child {
        justify-self: start;
        text-align: left;
    }
`;

interface SavingsPieProps {
    data: { label: string; value: number }[];
}
export function SavingsPieChart(props: SavingsPieProps) {
    const { data } = props;

    return (
        <Center sx={{ height: 200, width: 250 }}>
            <PieChart
                data={data}
                groups={['label']}
                values={['value']}
                settings={{
                    valueFormat: 'money',
                }}
                mode="user-configured"
            />
        </Center>
    );
}
interface MonthlySavingsBarProps {
    data: { date: string; group: SavingsGroup; value: number }[];
}
export function MonthlySavingsChart(props: MonthlySavingsBarProps) {
    const { colors } = useMantineTheme();
    const { data } = props;
    const barSettings = {
        orientation: 'Vertical',
        format: 'money',
        xFormat: 'short-month',
        noWrapper: true,
        margin: { top: 0 },
        reaggOptions: {
            sortBy: 'label',
            sortDir: 'asc',
        },
        chartColors: ({ dimensions }) => (dimensions[1] === 'Commitment Waste' ? '#AF510499' : colors.primary[5]),
    } as UserConfiguredBarChartSettings;
    return <BarChart settings={barSettings} groups={['date', 'group']} values={['value']} data={data} mode="user-configured" />;
}

interface SavingsBarProps {
    data: { label: string; value: number }[];
}
function SavingsBarChart(props: SavingsBarProps) {
    const { data } = props;
    const barSettings = {
        orientation: 'Vertical',
        format: 'percent-full-length',
        noWrapper: true,
        features: ['bar-totals'],
        margin: { top: 20 },
    } as UserConfiguredBarChartSettings;
    return <BarChart settings={barSettings} groups={['label']} values={['value']} data={data} mode="user-configured" />;
}
